import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { Dialog } from '@/components/AppDialog'
import { api } from '@/api/client'
import { ErrorBanner, formatDateTime } from '@/components/common'

type RunStatus = {
  state: 'IDLE' | 'RUNNING' | 'FINISHED' | 'FAILED'
  dryRun: boolean
  database?: string
  company?: string
  startedBy?: string
  startedAt?: string
  finishedAt?: string
  tally: Record<string, number>
  lines: string[]
  error?: string
}

type ImportView = {
  configured: boolean
  databases: string[]
  company: string
  run: RunStatus
}

const BANQUET = 'ECEEBILL_SANGETHAS_BANQUET_HALL'
const SWEETS = 'ECEEBILL_SAGETHAS_SWEET_ORDER'
const NAMES: Record<string, string> = { [BANQUET]: 'Banquet hall', [SWEETS]: 'Sweets orders' }

const isoDay = (date: Date) => date.toISOString().slice(0, 10)
const yesterday = () => isoDay(new Date(Date.now() - 24 * 3600 * 1000))

/** A line that needs somebody's eye: refused, left out, or priced away from its bill. */
const isProblem = (line: string) => /SKIPPED|FAILED|differs|stopped|nothing done/.test(line)

/**
 * Bringing bookings across from the old ECEEBILL billing system - only when an
 * administrator asks, here.
 *
 * <p>It used to run itself whenever the application started with a setting
 * left on, which quietly turned it into a sync. Now a dry run shows what an
 * import would do and keeps nothing; an import does it, in the company chosen
 * at the top of the screen. Running it twice adds nothing twice.</p>
 */
export function EceebillImportTab() {
  const queryClient = useQueryClient()
  const view = useQuery({
    queryKey: ['eceebill-import'],
    queryFn: () => api.get<ImportView>('/api/v1/platform/eceebill-import'),
    // Watched while a run is going; left alone otherwise.
    refetchInterval: (query) => (query.state.data?.run.state === 'RUNNING' ? 3000 : false),
  })

  const [database, setDatabase] = useState(BANQUET)
  const [ordersAfter, setOrdersAfter] = useState(yesterday())
  const [withBills, setWithBills] = useState(false)
  const [billsSince, setBillsSince] = useState(`${new Date().getFullYear()}-01-01`)
  const [billsAs, setBillsAs] = useState<'ORDERS' | 'NOTES'>('ORDERS')
  const [numberPrefix, setNumberPrefix] = useState('')
  const [historyLabel, setHistoryLabel] = useState('Banquet')
  const [serviceOverrides, setServiceOverrides] = useState('')
  const [branchOverrides, setBranchOverrides] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [problemsOnly, setProblemsOnly] = useState(true)

  // Each database's own sensible settings, as the two imports were first run.
  useEffect(() => {
    const sweets = database === SWEETS
    setNumberPrefix(sweets ? 'SW-' : '')
    setHistoryLabel(sweets ? 'Sweets' : 'Banquet')
    setServiceOverrides(sweets ? 'TAKEAWAY=COUNTER_SALE' : '')
  }, [database])

  const start = useMutation({
    mutationFn: (dryRun: boolean) =>
      api.post<RunStatus>('/api/v1/platform/eceebill-import/run', {
        database,
        dryRun,
        ordersAfter,
        billsSince: withBills ? billsSince : null,
        billsAsOrders: billsAs === 'ORDERS',
        numberPrefix,
        historyLabel,
        serviceOverrides,
        branchOverrides,
      }),
    onSuccess: () => {
      setConfirming(false)
      void queryClient.invalidateQueries({ queryKey: ['eceebill-import'] })
    },
  })

  const run = view.data?.run
  const running = run?.state === 'RUNNING'
  const lines = useMemo(
    () => (run?.lines ?? []).filter((line) => !problemsOnly || isProblem(line) || /finished|matched/.test(line)),
    [run, problemsOnly],
  )

  if (view.isLoading) return <LinearProgress />

  return (
    <Stack spacing={2}>
      <ErrorBanner error={view.error ?? start.error} />
      <Typography variant="body2" color="text.secondary">
        Brings bookings across from the old ECEEBILL system. It runs only when started here - never
        on its own. A dry run shows what an import would do and keeps nothing; running an import
        twice adds nothing twice.
      </Typography>

      {view.data && !view.data.configured ? (
        <Alert severity="warning">
          The old ECEEBILL server is not set up. Put LEGACY_DB_URL, LEGACY_DB_USER and
          LEGACY_DB_PASSWORD in the server&apos;s .env and restart the app.
        </Alert>
      ) : null}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              size="small"
              label="Old database"
              value={database}
              onChange={(event) => setDatabase(event.target.value)}
            >
              {(view.data?.databases ?? [BANQUET, SWEETS]).map((name) => (
                <MenuItem key={name} value={name}>
                  {NAMES[name] ?? name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Imports into"
              value={view.data?.company ?? ''}
              InputProps={{ readOnly: true }}
              helperText="The company you are working in - switch it at the top of the screen to change"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Upcoming bookings with a function after"
              value={ordersAfter}
              onChange={(event) => setOrdersAfter(event.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Booked, not yet billed - brought in as confirmed orders with their advances"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Number prefix"
              value={numberPrefix}
              onChange={(event) => setNumberPrefix(event.target.value)}
              helperText="Put before old order and receipt numbers - SW- for sweets, blank for banquet"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={withBills} onChange={(event) => setWithBills(event.target.checked)} />}
              label="Also bring in bookings already billed"
            />
          </Grid>
          {withBills ? (
            <>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Billed with a function from"
                  value={billsSince}
                  onChange={(event) => setBillsSince(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={billsAs}
                  onChange={(_event, value) => value && setBillsAs(value)}
                >
                  <ToggleButton value="ORDERS">As completed orders</ToggleButton>
                  <ToggleButton value="NOTES">As customer notes</ToggleButton>
                </ToggleButtonGroup>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Note label"
                  value={historyLabel}
                  onChange={(event) => setHistoryLabel(event.target.value)}
                  helperText={'e.g. "Sweets bill PPS/1075"'}
                />
              </Grid>
            </>
          ) : null}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Service overrides (only if the dry run asks)"
              value={serviceOverrides}
              onChange={(event) => setServiceOverrides(event.target.value)}
              helperText="e.g. TAKEAWAY=COUNTER_SALE"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Branch overrides (only if the dry run asks)"
              value={branchOverrides}
              onChange={(event) => setBranchOverrides(event.target.value)}
              helperText="e.g. PPS=PPSS,KBP=KBPR"
            />
          </Grid>
        </Grid>
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            disabled={running || start.isPending || !view.data?.configured}
            onClick={() => start.mutate(true)}
          >
            Dry run
          </Button>
          <Button
            variant="contained"
            disabled={running || start.isPending || !view.data?.configured}
            onClick={() => setConfirming(true)}
          >
            Import
          </Button>
        </Stack>
      </Paper>

      {run && run.state !== 'IDLE' ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            <Typography variant="subtitle2">
              {run.dryRun ? 'Dry run' : 'Import'} of {NAMES[run.database ?? ''] ?? run.database} into {run.company}
            </Typography>
            <Chip
              size="small"
              label={run.state === 'RUNNING' ? 'Running…' : run.state === 'FAILED' ? 'Stopped' : 'Finished'}
              color={run.state === 'FAILED' ? 'error' : run.state === 'RUNNING' ? 'info' : 'success'}
            />
            <Typography variant="caption" color="text.secondary">
              by {run.startedBy}, started {formatDateTime(run.startedAt)}
              {run.finishedAt ? `, finished ${formatDateTime(run.finishedAt)}` : ''}
            </Typography>
          </Stack>
          {running ? <LinearProgress sx={{ mb: 1 }} /> : null}
          {run.error ? <Alert severity="error" sx={{ mb: 1 }}>{run.error}</Alert> : null}
          {Object.keys(run.tally ?? {}).length > 0 ? (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              {Object.entries(run.tally).map(([outcome, count]) => (
                <Chip key={outcome} size="small" variant="outlined" label={`${outcome}: ${count}`} />
              ))}
            </Stack>
          ) : null}
          <FormControlLabel
            control={<Checkbox size="small" checked={problemsOnly} onChange={(event) => setProblemsOnly(event.target.checked)} />}
            label={<Typography variant="body2">Show only what needs a look</Typography>}
          />
          <Box
            sx={{
              maxHeight: 420,
              overflow: 'auto',
              fontFamily: 'monospace',
              fontSize: 12,
              bgcolor: 'action.hover',
              p: 1,
              borderRadius: 1,
            }}
          >
            {lines.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {running ? 'Working…' : 'Nothing that needs a look.'}
              </Typography>
            ) : (
              lines.map((line, index) => (
                <Box key={index} sx={{ color: isProblem(line) ? 'error.main' : 'text.primary', whiteSpace: 'pre-wrap' }}>
                  {line.replace(/^ECEEBILL /, '')}
                </Box>
              ))
            )}
          </Box>
        </Paper>
      ) : null}

      <Dialog open={confirming} onClose={() => setConfirming(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import from ECEEBILL?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This brings bookings from {NAMES[database]} into {view.data?.company} and keeps them:
            upcoming bookings after {ordersAfter}
            {withBills ? `, and billed ones from ${billsSince} as ${billsAs === 'ORDERS' ? 'completed orders' : 'notes'}` : ''}.
            Nothing is sent to any customer. Run a dry run first if you have not.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)}>Cancel</Button>
          <Button variant="contained" disabled={start.isPending} onClick={() => start.mutate(false)}>
            Import
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}
